import { Link } from 'react-router-dom';
import { Play, Square, type LucideIcon } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@proerp/ui';
import { usePermissions } from '@/lib/useCurrentUser';
import { elapsedSeconds, formatDurationShort } from '@/features/time-tracking/duration';
import { QUICK_ACTIONS } from './quickActions';
import { useStopRunningTimer } from './useStopRunningTimer';

const ROW =
  'flex min-h-14 w-full select-none touch-manipulation items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors active:bg-muted hover:bg-muted/60 disabled:opacity-50';

function Row({ icon: Icon, label, hint }: { icon: LucideIcon; label: string; hint: string }) {
  return (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
        <Icon className="size-4" strokeWidth={1.5} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] text-foreground">{label}</span>
        <span className="block truncate text-xs text-muted-foreground">{hint}</span>
      </span>
    </>
  );
}

/**
 * „+" der mobilen Leiste: alles Anlegen an einer Stelle — Zeit starten/stoppen
 * und die Schnellaktionen der Befehls-Palette (`QUICK_ACTIONS`, gleiche Rechte).
 * Anlegen öffnet die Zielseite mit `?neu=1`, dort geht der Dialog auf.
 */
export function CreateSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { can } = usePermissions();
  const timer = useStopRunningTimer();
  const actions = QUICK_ACTIONS.filter((action) => can(action.permission));
  const close = () => onOpenChange(false);
  const runningSince = timer.running?.startTime ? formatDurationShort(elapsedSeconds(timer.running.startTime, Date.now())) : '';
  const runningOn = timer.running?.ttProjectName ?? timer.running?.projectName ?? '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88svh] gap-0 overflow-y-auto rounded-t-2xl p-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))] tablet:hidden">
        <SheetHeader className="px-4 pb-1 pt-4 text-left">
          <SheetTitle className="text-md font-semibold text-foreground">Neu</SheetTitle>
          <SheetDescription className="sr-only">Zeit erfassen oder etwas Neues anlegen.</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col px-2 pb-1">
          {timer.isAllowed &&
            (timer.isRunning ? (
              <button
                type="button"
                className={ROW}
                disabled={timer.isStopping}
                onClick={async () => {
                  await timer.stop();
                  close();
                }}
              >
                <Row icon={Square} label="Zeit stoppen" hint={`läuft seit ${runningSince}${runningOn ? ` · ${runningOn}` : ''}`} />
              </button>
            ) : (
              <Link to="/zeiterfassung" onClick={close} className={ROW}>
                <Row icon={Play} label="Zeit starten" hint="In der Zeiterfassung" />
              </Link>
            ))}
          {actions.map((action) => (
            <Link key={action.id} to={action.route} onClick={close} className={ROW}>
              <Row icon={action.icon} label={action.label} hint={action.hint} />
            </Link>
          ))}
          {!timer.isAllowed && actions.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Für dein Konto gibt es hier nichts anzulegen.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
